import { useGet, useResource } from "../../lib/hooks";
import { auDate } from "../../lib/api";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { AuDateInput } from "../AuDateInput";

const SEL = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export const CONTACT_FIELDS = [
  ["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"],
  ["position_title", "Position title"], ["address", "Street address"], ["suburb", "Suburb"], ["state", "State"], ["postcode", "Postcode"],
  ["client_id", "Client ID"], ["date_of_birth", "Date of birth"], ["ndis_number", "NDIS number"], ["ndis_plan_management", "NDIS plan management"],
  ["ndis_plan_end", "NDIS plan end date"], ["notes", "Notes"],
];

export function ContactsTab({ f, set }) {
  const fields = (f.fields || []).filter((x) => !["heading", "instructions"].includes(x.type));
  const map = f.contact_map || {};
  const tabVal = f.is_referral ? "referral" : f.contact_tab;
  return (
    <div className="space-y-4" data-testid="contacts-tab">
      <div className="space-y-1"><Label>Create or update a contact from each submission in</Label>
        <select className={SEL} value={tabVal} onChange={(e) => { const v = e.target.value; set("is_referral", v === "referral"); if (v !== "referral") set("contact_tab", v); }} data-testid="form-contact-tab">
          <option value="referral">Contacts · Referrals (review, then transfer)</option><option value="general">Contacts · General</option><option value="private">Contacts · Private</option><option value="none">Don't create contacts</option>
        </select></div>
      {tabVal !== "none" && tabVal !== "referral" && <>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.contact_update !== false} onCheckedChange={(v) => set("contact_update", v)} data-testid="contact-update-switch" />Update the existing contact when the email or phone already matches</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.contact_as_client} onCheckedChange={(v) => set("contact_as_client", v)} data-testid="contact-as-client-switch" />Mark the contact as a Client (e.g. NDIS participants)</label>
        <div className="rounded-md border border-white/10 p-3 space-y-2" data-testid="contact-map">
          <div className="text-xs text-muted-foreground">Choose which answer fills each contact detail. Leave a row blank to skip it. An email or phone answer is needed to match or create the contact.</div>
          {CONTACT_FIELDS.map(([k, l]) => (
            <div key={k} className="grid grid-cols-2 gap-2 items-center">
              <span className="text-sm">{l}</span>
              <select className={SEL} value={map[k] || ""} onChange={(e) => set("contact_map", { ...map, [k]: e.target.value })} data-testid={`contact-map-${k}`}>
                <option value="">Don't fill</option>{fields.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </div>
          ))}
        </div>
      </>}
    </div>
  );
}

const STATUSES = [["draft", "Draft"], ["under_review", "Under review"], ["approved", "Approved (ready for staff)"], ["archived", "Archived"]];

function Picker({ items, ids, onChange, tid, label }) {
  const sel = new Set(ids || []);
  return (
    <div className="ml-7 mt-1 flex flex-wrap gap-2" data-testid={tid}>
      {(items || []).map((x) => (
        <label key={x.id} className={`cursor-pointer rounded-full border px-2.5 py-1 text-xs ${sel.has(x.id) ? "border-[#C2A462] bg-[#C2A462]/15 text-[#EBD6A0]" : "border-white/15 text-slate-300"}`}>
          <input type="checkbox" className="sr-only" checked={sel.has(x.id)} onChange={(e) => onChange(e.target.checked ? [...sel, x.id] : [...sel].filter((i) => i !== x.id))} data-testid={`${tid}-${x.id}`} />{x.name || x.email}
        </label>
      ))}
      {!items?.length && <span className="text-xs text-muted-foreground">No {label} set up yet.</span>}
    </div>
  );
}

export function DocumentTab({ f, set, accountName }) {
  const m = f.doc_meta || {};
  const s = f.share || {};
  const setM = (k, v) => set("doc_meta", { ...m, [k]: v });
  const setS = (k, v) => set("share", { ...s, [k]: v });
  const divisions = useResource("divisions").items;
  const groups = useResource("staff_groups").items;
  const [users] = useGet("/users?include_owner=1");
  const box = (k, label) => (
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-[#C2A462]" checked={!!s[k]} onChange={(e) => setS(k, e.target.checked)} data-testid={`share-${k}`} />{label}</label>
  );
  const date = (k, label) => <div className="space-y-1"><Label>{label}</Label><AuDateInput value={m[k] || ""} onChange={(v) => setM(k, v)} className={SEL} testid={`doc-${k}`} /></div>;
  return (
    <div className="space-y-5" data-testid="document-tab">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Created</Label><div className="text-sm text-slate-300" data-testid="doc-created">{m.created_by_name ? `${m.created_by_name} · ${auDate(m.creation_date)}` : "Stamped when you first save"}</div></div>
        <div className="space-y-1"><Label>Version number</Label><Input value={m.version || ""} onChange={(e) => setM("version", e.target.value)} placeholder="1.0" data-testid="doc-version" /></div>
        {date("ceo_approved_date", "Approved by CEO (date)")}
        {date("qs_approved_date", "Approved by Q&S Committee (date)")}
        {date("next_review_date", "Next review date")}
        <div className="space-y-1"><Label>Status</Label><select className={SEL} value={m.status || "draft"} onChange={(e) => setM("status", e.target.value)} data-testid="doc-status">{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
      </div>
      <div className="space-y-1"><Label>Copyright holder</Label><Input value={m.copyright_name || ""} onChange={(e) => setM("copyright_name", e.target.value)} placeholder={accountName || "Entity name"} data-testid="doc-copyright" />
        <p className="text-[11px] text-slate-500" data-testid="doc-copyright-preview">Printed on every page of the PDF: © {(m.copyright_name || "").trim() || accountName || "Entity name"} · Version {m.version || "1.0"} · {(m.ceo_approved_date || new Date().toISOString()).slice(0, 7).replace("-", "/")}. Leave blank to use the entity name.</p></div>
      <p className="text-[11px] text-slate-500">These details are saved to the Document Register in the Record Centre automatically. When the status is Approved, a printable PDF of the blank form is saved there too. Changing the version number keeps the previous PDF as an archived entry.</p>
      <div className="rounded-md border border-white/10 p-3 space-y-2" data-testid="share-settings">
        <Label>Shared Form Record: who can find and fill in this form in Document Essentials</Label>
        <p className="text-[11px] text-slate-500">Only shows once the status is Approved. Owners and administrators always see it.</p>
        {box("owner", "Owner")}{box("entity_admin", "Entity Admin")}{box("management_unit", "Management Unit (anyone in a group with \"Management\" in its name)")}
        {box("division", "Division")}{s.division && <Picker items={divisions} ids={s.division_ids} onChange={(v) => setS("division_ids", v)} tid="share-divisions" label="divisions" />}
        {box("group", "Group")}{s.group && <Picker items={groups} ids={s.group_ids} onChange={(v) => setS("group_ids", v)} tid="share-groups" label="groups" />}
        {box("all_staff", "All Staff")}
        {box("person", "Specific person")}{s.person && <Picker items={(users || []).filter((u) => !u.disabled)} ids={s.user_ids} onChange={(v) => setS("user_ids", v)} tid="share-people" label="people" />}
      </div>
    </div>
  );
}
